import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import type {
  GuidanceKind,
  MonitoringActivity,
  MonitoringAttestation,
} from './monitoring-types';

import { getDesignTokens } from '~/client/components/base/core';

const GUIDANCE_LABELS: Record<GuidanceKind, string> = {
  inform: 'Inform',
  warn: 'Warn',
  escalate: 'Escalate',
  decline: 'Decline',
  require_human_review: 'Human review',
};

interface ActivityChipProps {
  value: MonitoringActivity;
}

export const ActivityChip: React.FC<ActivityChipProps> = ({ value }) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  if (value === 'unavailable') {
    return (
      <Chip
        label="Signal unavailable"
        size="small"
        sx={{
          backgroundColor: 'transparent',
          color: t.textMuted,
          border: `1px solid ${t.borderSubtle}`,
        }}
      />
    );
  }

  const isActive = value === 'active';
  return (
    <Chip
      label={isActive ? 'Active' : 'Idle'}
      size="small"
      sx={{
        backgroundColor: isActive ? t.status.successBg : t.status.neutralBg,
        color: isActive ? t.status.success : t.status.neutral,
        border: 'none',
        fontWeight: 500,
      }}
    />
  );
};

interface AttestationChipProps {
  value: MonitoringAttestation;
}

export const AttestationChip: React.FC<AttestationChipProps> = ({ value }) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  if (value === 'not_applicable') {
    return null;
  }

  const isGap = value === 'unverified';
  return (
    <Chip
      label={isGap ? 'Attestation gap' : 'Attested ✓'}
      size="small"
      sx={{
        backgroundColor: isGap ? t.status.warningBg : t.status.successBg,
        color: isGap ? t.status.warning : t.status.success,
        border: 'none',
        fontWeight: 500,
      }}
    />
  );
};

interface GuidanceChipProps {
  value: GuidanceKind;
}

export const GuidanceChip: React.FC<GuidanceChipProps> = ({ value }) => {
  const theme = useTheme();
  const t = React.useMemo(
    () => getDesignTokens(theme.palette.mode, theme.accentPalette),
    [theme.palette.mode, theme.accentPalette]
  );

  return (
    <Chip
      label={GUIDANCE_LABELS[value] ?? value}
      size="small"
      sx={{
        backgroundColor: t.status.infoBg,
        color: t.status.info,
        border: 'none',
      }}
    />
  );
};
